/**
 * Unified Sidebar Component
 * Combines navigation with context-specific content (chat history, image/video jobs, documents)
 * Features glass morphism design with red accent theme
 * Validates: Requirements 20.3 - Unified navigation and history
 */

import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useChatStore } from '../../stores/chatStore';
import { useImageStore } from '../../stores/imageStore';
import { useVideoStore } from '../../stores/videoStore';
import { useDocumentStore } from '../../stores/documentStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface UnifiedSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'chat' | 'images' | 'videos' | 'documents' | 'settings';

/**
 * Tab Button Component
 */
interface TabButtonProps {
  id: TabType;
  icon: React.ComponentType;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ icon: Icon, label, isActive, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
        isActive
          ? 'bg-accent-soft border border-accent-border text-white'
          : 'text-gray-400 hover:bg-white/5 hover:text-white'
      }`}
      style={{
        borderColor: isActive ? THEME.accentBorder : 'transparent',
        backgroundColor: isActive ? THEME.accentSoft : undefined,
      }}
    >
      <Icon />
      <span className="font-medium text-sm">{label}</span>
    </button>
  );
}

/**
 * Chat History Item Component
 */
interface ChatHistoryItemProps {
  id: string;
  title: string;
  date: string;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function ChatHistoryItem({ title, date, isActive, onSelect, onDelete }: ChatHistoryItemProps) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div
      className={`group relative px-3 py-2 rounded-lg cursor-pointer transition-all ${
        isActive ? 'bg-accent-soft border border-accent-border' : 'hover:bg-white/5'
      }`}
      onClick={onSelect}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      style={{
        backgroundColor: isActive ? THEME.accentSoft : undefined,
        borderColor: isActive ? THEME.accentBorder : 'transparent',
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-sm truncate ${isActive ? 'text-white' : 'text-gray-300'}`}>
            {title}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">{date}</p>
        </div>
        {showActions && !isActive && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Delete session"
          >
            <Icons.Trash size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Main Unified Sidebar Component
 */
export function UnifiedSidebar({ isOpen, onClose }: UnifiedSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const performLogout = useAuthStore((state) => state.performLogout);

  // Stores
  const { sessions, currentSessionId, loadSession, deleteSession, clearMessages } = useChatStore();
  const { jobs: imageJobs } = useImageStore();
  const { jobs: videoJobs } = useVideoStore();
  const { documents } = useDocumentStore();

  // Determine active tab from route
  const getActiveTab = (): TabType => {
    const path = location.pathname;
    if (path.startsWith('/chat')) return 'chat';
    if (path.startsWith('/images')) return 'images';
    if (path.startsWith('/videos')) return 'videos';
    if (path.startsWith('/documents')) return 'documents';
    if (path.startsWith('/settings')) return 'settings';
    return 'chat';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getActiveTab());
  const [searchQuery, setSearchQuery] = useState('');

  const handleTabClick = useCallback((tab: TabType) => {
    setActiveTab(tab);
    const routes: Record<TabType, string> = {
      chat: '/chat',
      images: '/images',
      videos: '/videos',
      documents: '/documents',
      settings: '/settings',
    };
    navigate(routes[tab]);
    // Close sidebar on mobile
    if (window.innerWidth < 1024) {
      onClose();
    }
  }, [navigate, onClose]);

  const handleLogout = async () => {
    await performLogout();
    navigate('/login', { replace: true });
  };

  const handleNewChat = () => {
    clearMessages();
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const handleSelectChatSession = (sessionId: string) => {
    loadSession(sessionId);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  // Filter sessions by search query
  const filteredSessions = sessions.filter((session) =>
    (session.title || 'New Chat').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full border-r transition-all ease-smooth lg:static lg:z-auto ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{
          width: '280px',
          minWidth: '280px',
          backgroundColor: THEME.glassSurface,
          borderColor: THEME.border,
          backdropFilter: 'blur(20px)',
          transitionDuration: '300ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header with Logo */}
          <div
            className="flex items-center gap-3 px-6 py-6 border-b"
            style={{ borderColor: THEME.border }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: THEME.accent }}
            >
              <Icons.Sparkles size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-white font-bold text-base" style={{ letterSpacing: '-0.5px' }}>
                NEXUS AI
              </h1>
              <p
                className="text-xs uppercase"
                style={{ color: THEME.textMuted, letterSpacing: '1px' }}
              >
                Intelligence Platform
              </p>
            </div>
          </div>

          {/* New Chat / New Item Button */}
          {activeTab === 'chat' && (
            <div className="px-4 pt-4">
              <button
                onClick={handleNewChat}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 border font-semibold text-sm hover:-translate-y-0.5"
                style={{
                  backgroundColor: THEME.accentSoft,
                  borderColor: THEME.accentBorder,
                  color: THEME.accentColor,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = THEME.accentHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = THEME.accentSoft;
                }}
              >
                <Icons.Plus size={18} />
                <span>New Conversation</span>
              </button>
            </div>
          )}

          {/* Search (for chat history) */}
          {activeTab === 'chat' && (
            <div className="px-4 pt-3">
              <div className="relative">
                <Icons.Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: THEME.textMuted }}
                  size={14}
                />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 border transition-colors focus:outline-none"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    borderColor: THEME.border,
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = THEME.accentBorder;
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = THEME.border;
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)';
                  }}
                />
              </div>
            </div>
          )}

          {/* Navigation Tabs */}
          <nav className="px-3 pt-4 pb-3 space-y-1">
            <p
              className="px-4 mb-2 text-xs font-semibold uppercase"
              style={{ color: THEME.textMuted, letterSpacing: '0.5px' }}
            >
              Environments
            </p>
            <TabButton
              id="chat"
              icon={Icons.Message}
              label="Text Chat"
              isActive={activeTab === 'chat'}
              onClick={() => handleTabClick('chat')}
            />
            <TabButton
              id="images"
              icon={Icons.Image}
              label="Images"
              isActive={activeTab === 'images'}
              onClick={() => handleTabClick('images')}
            />
            <TabButton
              id="videos"
              icon={Icons.Video}
              label="Videos"
              isActive={activeTab === 'videos'}
              onClick={() => handleTabClick('videos')}
            />
            <TabButton
              id="documents"
              icon={Icons.Document}
              label="Documents & RAG"
              isActive={activeTab === 'documents'}
              onClick={() => handleTabClick('documents')}
            />
          </nav>

          {/* Dynamic Content Area */}
          <div className="flex-1 overflow-y-auto px-3 pb-3">
            {/* Chat History */}
            {activeTab === 'chat' && (
              <div className="space-y-1">
                <p
                  className="px-3 mb-2 text-xs font-semibold uppercase"
                  style={{ color: THEME.textMuted, letterSpacing: '0.5px' }}
                >
                  Recent
                </p>
                {filteredSessions.length === 0 ? (
                  <div className="px-3 py-8 text-center">
                    <p className="text-sm" style={{ color: THEME.textMuted }}>
                      {searchQuery ? 'No conversations found' : 'No conversations yet'}
                    </p>
                  </div>
                ) : (
                  filteredSessions.map((session) => (
                    <ChatHistoryItem
                      key={session.id}
                      id={session.id}
                      title={session.title || 'New Chat'}
                      date={new Date(session.createdAt).toLocaleDateString()}
                      isActive={session.id === currentSessionId}
                      onSelect={() => handleSelectChatSession(session.id)}
                      onDelete={() => deleteSession(session.id)}
                    />
                  ))
                )}
              </div>
            )}

            {/* Image Jobs History */}
            {activeTab === 'images' && (
              <div className="space-y-2">
                <p
                  className="px-3 mb-2 text-xs font-semibold uppercase"
                  style={{ color: THEME.textMuted, letterSpacing: '0.5px' }}
                >
                  Recent Jobs
                </p>
                {imageJobs.length === 0 ? (
                  <div className="px-3 py-8 text-center">
                    <Icons.Image size={32} style={{ color: THEME.textMuted, opacity: 0.3 }} className="mx-auto mb-2" />
                    <p className="text-sm" style={{ color: THEME.textMuted }}>
                      No images generated yet
                    </p>
                  </div>
                ) : (
                  imageJobs.slice(0, 10).map((job) => (
                    <div
                      key={job.id}
                      className="px-3 py-2 rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                    >
                      <p className="text-sm text-gray-300 truncate">{job.prompt}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(job.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Video Jobs History */}
            {activeTab === 'videos' && (
              <div className="space-y-2">
                <p
                  className="px-3 mb-2 text-xs font-semibold uppercase"
                  style={{ color: THEME.textMuted, letterSpacing: '0.5px' }}
                >
                  Recent Jobs
                </p>
                {videoJobs.length === 0 ? (
                  <div className="px-3 py-8 text-center">
                    <Icons.Video size={32} style={{ color: THEME.textMuted, opacity: 0.3 }} className="mx-auto mb-2" />
                    <p className="text-sm" style={{ color: THEME.textMuted }}>
                      No videos generated yet
                    </p>
                  </div>
                ) : (
                  videoJobs.slice(0, 10).map((job) => (
                    <div
                      key={job.id}
                      className="px-3 py-2 rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                    >
                      <p className="text-sm text-gray-300 truncate">{job.prompt}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(job.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Documents List */}
            {activeTab === 'documents' && (
              <div className="space-y-2">
                <p
                  className="px-3 mb-2 text-xs font-semibold uppercase"
                  style={{ color: THEME.textMuted, letterSpacing: '0.5px' }}
                >
                  Uploaded Documents
                </p>
                {documents.length === 0 ? (
                  <div className="px-3 py-8 text-center">
                    <Icons.File size={32} style={{ color: THEME.textMuted, opacity: 0.3 }} className="mx-auto mb-2" />
                    <p className="text-sm" style={{ color: THEME.textMuted }}>
                      No documents uploaded yet
                    </p>
                  </div>
                ) : (
                  documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="px-3 py-2 rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <Icons.File size={16} style={{ color: THEME.accentColor }} className="mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-300 truncate">{doc.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {(doc.size / 1024 / 1024).toFixed(2)} MB • {doc.chunkCount} chunks
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Settings - No additional content needed */}
            {activeTab === 'settings' && (
              <div className="px-3 py-8 text-center">
                <Icons.Settings size={32} style={{ color: THEME.textMuted, opacity: 0.3 }} className="mx-auto mb-2" />
                <p className="text-sm" style={{ color: THEME.textMuted }}>
                  Configure your settings on the right
                </p>
              </div>
            )}
          </div>

          {/* User Section (Fixed at bottom) */}
          <div
            className="border-t p-4"
            style={{ borderColor: THEME.border }}
          >
            <div
              className="p-3 rounded-xl mb-3"
              style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold"
                  style={{ background: 'linear-gradient(135deg, #333, #222)' }}
                >
                  {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs truncate" style={{ color: THEME.textMuted }}>
                    {user?.email || ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Settings Button */}
            <button
              onClick={() => handleTabClick('settings')}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg transition-colors hover:bg-white/5 mb-2"
              style={{ color: activeTab === 'settings' ? THEME.accentColor : THEME.textMuted }}
            >
              <Icons.Settings size={16} />
              <span>Settings</span>
            </button>

            {/* Sign Out Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg transition-colors hover:bg-white/5"
              style={{ color: THEME.textMuted }}
            >
              <Icons.User size={16} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
